import { useEffect, useMemo } from 'react';
import { bindActionCreators } from 'redux';
import Jaws from 'oms-jaws';

import * as Actions from 'actions/jaws';
import { BASE_URL, SECURE_BASE_URL } from 'constants/Jaws';
import { useSelector } from 'react-redux';
import { selectIsUserLoggedIn } from '../state/user/selectors';
import {
  jawsLog,
  jawsRegister,
  watchJawsVisibility,
} from './jawsDebug';

const MAX_RECONNECT_ATTEMPTS = 5;
let reconnectTimer = null;

function reconnect(jaws, reconnectAttempts = 0) {
  let sleep;
  if (reconnectAttempts <= MAX_RECONNECT_ATTEMPTS) {
    sleep = reconnectAttempts * 1000;
  } else {
    sleep = 0;
    // Bilet w jedna strone: od tego momentu streaming jest martwy az do przeladowania strony.
    jaws.useWebSockets = false;
    jawsLog('configureJaws', 'WYCZERPANO PROBY - useWebSockets=false na stale', {
      proby: reconnectAttempts,
    });
  }
  jawsLog('configureJaws', 'reconnect', { proba: reconnectAttempts, zaMs: sleep });

  clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => {
    if (jaws.state === 'disconnected') {
      jaws.reconnect();
    }
  }, sleep);
}

export default function useConfigureJaws(dispatch, useWebSockets) {
  const isLoggedIn = useSelector(selectIsUserLoggedIn);

  // Instancja musi powstawac raz na (isLoggedIn, useWebSockets). Wczesniej `new Jaws(...)`
  // stalo gole w ciele hooka, wiec KAZDY render otwieral kolejne gniazdo WebSocket, a
  // liczniki reconnectAttempts/pingInterval zyly w domknieciu porzucanym przy nastepnym
  // renderze - dlatego ponawianie polaczenia nie mialo szans dzialac spojnie.
  const jaws = useMemo(() => {
    const instance = new Jaws(
      isLoggedIn ? SECURE_BASE_URL : BASE_URL,
      useWebSockets,
    );
    const actions = bindActionCreators(Actions, dispatch);

    jawsRegister('configureJaws', instance);
    actions.setInstance(instance);

    let reconnectAttempts = 0;
    let pingTimestamp = null;
    let pingTimeout = null;
    let pingInterval = false;

    // TODO Helge fix these so reconnect works
    let inactive = null;
    let connectionClosedEventSent = null;

    instance.onOpen = () => {
      jawsLog('configureJaws', 'onOpen - polaczono');
      reconnectAttempts = 0;
      actions.connectionEstablished();

      if (instance.useWebSockets && !pingInterval) {
        pingInterval = setInterval(() => {
          pingTimeout = setTimeout(() => {
            clearInterval(pingInterval);
            pingInterval = null;
            instance.disconnect();
            reconnect(instance, ++reconnectAttempts);
          }, 1000);
          pingTimestamp = Date.now();
          instance.ping();
        }, 30000);
      }
    };

    instance.bind('pong', () => {
      const delay = Date.now() - pingTimestamp;
      if (delay > 1000) {
        clearInterval(pingInterval);
        pingInterval = null;
        instance.disconnect();
        reconnect(instance, ++reconnectAttempts);
      }
      if (pingTimeout) {
        clearTimeout(pingTimeout);
      }
    });

    instance.bind('connection_closed', event => {
      // Uwaga: obslugiwane sa TYLKO kody 4503, 1001 i 1006. Kazdy inny (np. 1000 - normalne
      // zamkniecie) nie wywoluje zadnego ponowienia. Log pokaze, ktory kod naprawde przychodzi.
      jawsLog('configureJaws', 'connection_closed', { code: event && event.code });
      if (event.code === 4503) {
        instance.useWebSockets = false;
        reconnect(instance, ++reconnectAttempts);
      }

      if (event.code === 1001 || event.code === 1006) {
        if (!connectionClosedEventSent) {
          actions.connectionClosed();
        }
        if (!inactive) {
          reconnect(instance, ++reconnectAttempts);
        }
      }
    });

    return instance;
  }, [isLoggedIn, useWebSockets, dispatch]);

  // Gdy useMemo utworzy nowa instancje (zmiana isLoggedIn), stara musi zostac rozlaczona.
  // Bez tego jej gniazdo zostawalo otwarte do konca zycia strony - w Network bylo widac
  // polaczenie, przez ktore nic juz nie leci.
  useEffect(() => {
    watchJawsVisibility();
    return () => {
      if (typeof jaws.disconnect === 'function') {
        jaws.disconnect();
      }
    };
  }, [jaws]);

  return jaws;
}
